import type {Repository} from '@github-ui/current-repository'
import type {RepoPullsDashboardAgentTaskRepository} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_pulls_dashboard'
import {Filter, type FilterProps} from '@github-ui/filter'
import {NewRepoAgentTaskButton} from '@github-ui/new-repo-agent-task-button/NewRepoAgentTaskButton'
import {noop} from '@github-ui/noop'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {DashboardSidebar} from '@github-ui/shared-dashboard-sidebar/DashboardSidebar'
import {DashboardSidebarCollapsible} from '@github-ui/shared-dashboard-sidebar/DashboardSidebarCollapsible'
import {SharedHeader} from '@github-ui/shared-dashboard-surface/components/shared/SharedHeader'
import {AgentIcon, MilestoneIcon, TagIcon} from '@primer/octicons-react'
import {Button, CounterLabel} from '@primer/react'
import {useId, useState} from 'react'

import {useRepoPullsSearch} from '../hooks/use-repo-pulls-dashboard'
import {repoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'
import {REPO_PULLS_SIDEBAR_LINKS} from './repo-pulls-sidebar-links'

const EMPTY_SAVED_VIEWS: never[] = []

export function RepoPullsDashboardHeader() {
  const {fetchedQuery, fetchResults, generateLinkPath, owner, repo, selectedLink} = useRepoPullsSearch()
  const [query, setQuery] = useState(fetchedQuery)
  const [previousFetchedQuery, setPreviousFetchedQuery] = useState(fetchedQuery)
  const providers = useFilterProviders({repositoryScope: owner && repo ? `${owner}/${repo}` : undefined})
  const selectedHeaderLink = selectedLink?.id === 'all' ? {...selectedLink, name: 'All pull requests'} : selectedLink

  if (previousFetchedQuery !== fetchedQuery) {
    setPreviousFetchedQuery(fetchedQuery)
    setQuery(fetchedQuery)
  }

  const filterProps: FilterProps = {
    id: 'repo-pulls-dashboard-filter',
    label: 'Search pull requests',
    variant: 'input',
    placeholder: 'Search pull requests',
    filterValue: query,
    providers,
    onChange: setQuery,
    onSubmit: filterQuery => fetchResults({q: filterQuery.raw}),
    settings: {groupAndKeywordSupport: true},
  }

  return (
    <SharedHeader
      dashboardType="pull-requests"
      links={REPO_PULLS_SIDEBAR_LINKS}
      selectedLink={selectedHeaderLink}
      onSelectLink={noop}
      savedViews={EMPTY_SAVED_VIEWS}
      generateLinkPath={generateLinkPath}
      optOutLocked
      showOptionsButton={false}
      showSavedViews={false}
      primaryAction={<RepoPullsDashboardActions />}
      filter={
        <div role="search" aria-label="Pull requests">
          <Filter {...filterProps} data-testid="repo-pulls-dashboard-filter" />
        </div>
      }
    />
  )
}

export function RepoPullsDashboardSidebar() {
  const {generateLinkPath, selectedLink} = useRepoPullsSearch()

  return (
    <DashboardSidebar
      dashboardType="pull-requests"
      links={REPO_PULLS_SIDEBAR_LINKS}
      selectedLink={selectedLink?.id || ''}
      onSelectLink={noop}
      savedViews={EMPTY_SAVED_VIEWS}
      generateLinkPath={generateLinkPath}
    />
  )
}

export function RepoPullsDashboardCollapsibleSidebar() {
  const {generateLinkPath, selectedLink} = useRepoPullsSearch()

  return (
    <DashboardSidebarCollapsible
      dashboardType="pull-requests"
      links={REPO_PULLS_SIDEBAR_LINKS}
      selectedLink={selectedLink?.id || ''}
      onSelectLink={noop}
      savedViews={EMPTY_SAVED_VIEWS}
      generateLinkPath={generateLinkPath}
    />
  )
}

function RepoPullsDashboardActions() {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const actions = layoutPayload?.actions
  const disabledReasonId = useId()
  if (!actions) return null

  const {agentTaskRepository, labels, milestones, newPullRequest} = actions
  return (
    <div className="d-flex flex-wrap gap-2">
      {labels ? (
        <Button as={Link} to={labels.url} leadingVisual={TagIcon}>
          Labels <CounterLabel>{labels.count}</CounterLabel>
        </Button>
      ) : null}
      {milestones ? (
        <Button as={Link} to={milestones.url} leadingVisual={MilestoneIcon}>
          Milestones <CounterLabel>{milestones.count}</CounterLabel>
        </Button>
      ) : null}
      {agentTaskRepository ? (
        <NewRepoAgentTaskButton
          repository={toRepository(agentTaskRepository)}
          icon={AgentIcon}
          label="New agent session"
          eventType="new_agent_task_button_repo_pulls"
        />
      ) : null}
      {newPullRequest?.url ? (
        <Button as={Link} to={newPullRequest.url} variant="primary">
          {newPullRequest.label}
        </Button>
      ) : newPullRequest?.disabledReason ? (
        <>
          <Button
            variant="primary"
            inactive
            aria-disabled="true"
            aria-describedby={disabledReasonId}
            title={newPullRequest.disabledReason}
          >
            {newPullRequest.label}
          </Button>
          <span id={disabledReasonId} className="sr-only">
            {newPullRequest.disabledReason}
          </span>
        </>
      ) : null}
    </div>
  )
}

function toRepository(repository: RepoPullsDashboardAgentTaskRepository): Repository {
  return {
    id: Number(repository.id),
    name: repository.name,
    ownerLogin: repository.ownerLogin,
    defaultBranch: repository.defaultBranch,
    createdAt: repository.createdAt,
    currentUserCanPush: repository.currentUserCanPush,
    isFork: repository.isFork,
    isEmpty: repository.isEmpty,
    ownerAvatar: repository.ownerAvatar,
    public: repository.public,
    private: repository.private,
    isOrgOwned: repository.isOrgOwned,
  }
}
